import TextField, { StandardTextFieldProps } from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import MUIAutocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { ReactNode } from 'react';

interface AutocompleteOption {
  label: string;
  value: string;
  category?: string;
}

interface AutocompleteEvent {
  target: { value: string | null };
}

interface AutocompleteProps extends Omit<StandardTextFieldProps, 'value'> {
  options: AutocompleteOption[];
  value?: string | number | AutocompleteOption | null;
  groupBy?: (option: AutocompleteOption) => string;
  onChange?: (e: AutocompleteEvent) => void;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutocompleteOption,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
}

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    fontWeight: 400,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff9e',
    fontSize: '14px',
    fontWeight: 400,
    '& .MuiOutlinedInput-input': {
      fontSize: '14px',
      fontWeight: 400,
    },
  },
}));

const CustomAutocomplete = (props: AutocompleteProps) => {
  const { options, groupBy, onChange, renderOption, value, ...textFieldProps } = props;

  const selectedOption =
    typeof value === 'string' || typeof value === 'number'
      ? options.find(option => String(option.value) === String(value)) ?? null
      : value ?? null;

  return (
    <MUIAutocomplete
      disablePortal
      renderInput={params => <CustomTextField variant="outlined" {...textFieldProps} {...params} />}
      options={options}
      value={selectedOption}
      getOptionLabel={option => option?.label ?? ''}
      isOptionEqualToValue={(option, selectedValue) =>
        String(option.value) === String(selectedValue.value)
      }
      groupBy={groupBy}
      onChange={(_event, newValue: AutocompleteOption | null) =>
        onChange?.({ target: { value: newValue?.value != null ? String(newValue.value) : null } })
      }
      renderOption={renderOption}
      componentsProps={{
        paper: {
          elevation: 0,
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            marginTop: '4px',
          },
        },
      }}
      ListboxProps={{
        sx: {
          padding: '4px 0',
          maxHeight: 280,
          '& .MuiAutocomplete-option': {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            minHeight: 40,
            padding: '8px 12px',
            color: '#595959',
            '&[aria-selected="true"]': {
              fontWeight: 400,
              backgroundColor: 'rgba(0, 108, 250, 0.08)',
            },
            '&.Mui-focused': {
              fontWeight: 400,
              backgroundColor: 'rgba(0, 108, 250, 0.05)',
            },
          },
        },
      }}
    />
  );
};

export const Autocomplete = styled(CustomAutocomplete)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
